import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { ComplianceControlId, ControlGate } from '@lizrd/contracts';
import { NIS2_CONTROL_BY_ID } from '@lizrd/contracts';
import { getComplianceSettings, updateComplianceSettings } from '../lib/api';
import { Card } from './ui/card';
import { Spinner } from './ui/spinner';
import { Toggle } from './ui/toggle';

const GATES: { value: ControlGate; label: string }[] = [
  { value: 'block', label: 'Block' },
  { value: 'warn', label: 'Warn' },
  { value: 'off', label: 'Off' },
];

/**
 * Per-domain NIS2 compliance settings (admins & managers). The master switch runs
 * the compliance checks on/off — mirrors the Reviews settings: when off, the
 * per-control policy is hidden and the Compliance page shows its educational
 * state; when on, a check runs right away (in the background) and the team tunes
 * how each NIS2 control is enforced.
 */
export function ComplianceSettings({ domainId }: { domainId: string }) {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ['compliance-settings', domainId],
    queryFn: () => getComplianceSettings(domainId),
    enabled: !!domainId,
  });
  const save = useMutation({
    mutationFn: (patch: Parameters<typeof updateComplianceSettings>[1]) =>
      updateComplianceSettings(domainId, patch),
    onSuccess: (updated) => {
      qc.setQueryData(['compliance-settings', domainId], updated);
      // Refresh the Compliance page (posture/gaps), the dashboard widgets, and the
      // domain records that carry complianceEnabled, so the toggle reflects at once.
      qc.invalidateQueries({ queryKey: ['compliance', domainId] });
      qc.invalidateQueries({ queryKey: ['org-compliance'] });
      qc.invalidateQueries({ queryKey: ['domain', domainId] });
      qc.invalidateQueries({ queryKey: ['domains'] });
    },
  });

  const s = settings.data;
  const enabled = s?.enabled ?? false;
  const busy = settings.isLoading || save.isPending;

  return (
    <div className="space-y-3">
      <div>
        <h3 className="font-display text-base font-bold text-bone">NIS2 compliance</h3>
        <p className="text-xs leading-relaxed text-muted">
          NIS2 (EU directive 2022/2555) requires in-scope organisations to continuously maintain
          technical risk-management measures across their estate. Lizrd maps the cloud-technical
          slice of those §30 measures to your knowledge graph — a posture score, a ranked gap list,
          and guardrails on cost recommendations. Evidence &amp; posture for the technical measures,
          not a certification; not legal advice.
        </p>
      </div>

      <Card className="p-4">
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="text-sm font-semibold text-bone">Run NIS2 compliance checks</div>
            <p className="mt-0.5 text-xs leading-relaxed text-muted">
              When on, Lizrd scores your estate against the NIS2 §30 measures, lists gaps, and
              guardrails every cost recommendation. Read-only; a check runs right away in the
              background and re-checks on every sync. The findings appear on the NIS2 Compliance
              page.
            </p>
          </div>
          <div className="flex flex-none items-center gap-2 pt-0.5">
            {busy && <Spinner size="sm" />}
            <Toggle
              checked={enabled}
              disabled={busy}
              onChange={(v) => save.mutate({ enabled: v })}
              label="Enable NIS2 compliance checks"
            />
          </div>
        </div>

        {enabled && s && (
          <div className="mt-4 border-t border-hairline pt-4">
            <div className="text-sm font-semibold text-bone">Controls &amp; enforcement</div>
            <p className="mt-0.5 text-xs leading-relaxed text-muted">
              How each NIS2 control is enforced. <b className="text-bone">Block</b> flags a cost
              action that would weaken it (and offers the compliant alternative);{' '}
              <b className="text-bone">Warn</b> is advisory; <b className="text-bone">Off</b> stops
              scoring it.
            </p>
            <div className="mt-2 overflow-hidden rounded-md border border-hairline">
              {(Object.keys(s.controls) as ComplianceControlId[]).map((id, i) => {
                const def = NIS2_CONTROL_BY_ID[id];
                const current = s.controls[id] ?? 'off';
                return (
                  <div
                    key={id}
                    className={`flex items-center justify-between gap-4 px-3 py-2.5 ${
                      i > 0 ? 'border-t border-hairline' : ''
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="text-sm font-medium text-bone">{def.label}</div>
                      <div className="font-mono text-[0.62rem] text-muted">{def.nis2Measure}</div>
                    </div>
                    <div className="inline-flex flex-none overflow-hidden rounded-sm border border-border">
                      {GATES.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          disabled={busy}
                          onClick={() => save.mutate({ controls: { [id]: opt.value } })}
                          className={`px-2.5 py-1 text-xs font-semibold transition-colors ${
                            current === opt.value
                              ? 'bg-bone text-surface'
                              : 'bg-transparent text-muted hover:bg-surface-2'
                          }`}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
